import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles, Clock, BadgePercent, ArrowRight, Info } from "lucide-react";
import { plans } from "@/data/pricing";
import { usePricing } from "@/context/PricingContext";
import { useDemoModal } from "@/context/DemoModalContext";
import FreePlanModal from "@/components/FreePlanModal";
import ContactPlanModal from "@/components/ContactPlanModal";

const fade = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.75 },
};

function PriceSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 w-24 rounded-lg bg-violet-light/60" />
    </div>
  );
}

export default function PricingSection() {
  const [annual, setAnnual] = useState(false);
  const navigate = useNavigate();
  const { loading, stale, prices, slotsRemaining, annualAvailable } = usePricing();
  // Oferta fundador forzada activa hasta nuevo aviso (ignora API hasta limpiar BD).
  const founderActive = true;
  const { openFreeModal } = useDemoModal();
  const [freeModalOpen, setFreeModalOpen] = useState(false);
  const [contactPlan, setContactPlan] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  function handleSelectPlan(plan) {
    // Plan Gratis: no pasa por Stripe; abre el modal de alta directa en la app.
    if (plan.id === "gratis") {
      setFreeModalOpen(true);
      return;
    }
    // Planes de pago (Esencial, Avanzado, Institucional): abren el modal de
    // contacto prellenado con el plan elegido.
    setContactPlan(plan.name.toLowerCase());
    setContactModalOpen(true);
  }

  // Precio mostrado, siempre desde el backend (sin fallback hardcodeado).
  function getPrice(planSlug) {
    const p = prices && prices[planSlug];
    if (!p) return null;
    if (annual && annualAvailable) {
      return (founderActive ? p.founder_annual_monthly : p.annual_monthly) ?? p.annual_monthly ?? null;
    }
    return (founderActive ? p.founder_monthly : p.monthly) ?? p.monthly ?? null;
  }

  // Precio regular (sin descuento fundador) para mostrar tachado.
  function getRegularPrice(planSlug) {
    if (!founderActive) return null;
    const p = prices && prices[planSlug];
    if (!p) return null;
    if (annual && annualAvailable) return p.annual_monthly ?? null;
    return p.monthly ?? null;
  }

  // Cargo anual total
  function getAnnualTotal(planSlug) {
    if (!prices || !prices[planSlug] || !annual || !annualAvailable) return null;
    const p = prices[planSlug];
    return founderActive ? p.founder_annual_total : p.annual_total;
  }

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 md:py-32 bg-white border-t border-violet-light"
    >
      <div className="mx-auto max-w-[1760px] px-4 md:px-6 xl:px-8">
        {/* Header */}
        <motion.div {...fade} className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gold mb-4">
            Precios
          </p>
          <h2
            data-testid="pricing-title"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink tracking-tight"
          >
            Un plan para cada etapa
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink-2 sm:text-xl">
            Sin sorpresas. Sin comisiones ocultas. Puedes cambiar de plan en cualquier momento.
          </p>
          <p className="mt-2 text-sm text-ink-3">
            Precios con IVA incluido.
          </p>
          {stale && (
            <p className="mt-2 text-[12px] text-ink-3/80">
              Mostrando los últimos precios guardados; puede que no estén actualizados.
            </p>
          )}
        </motion.div>

        {/* Toggle — solo si anual está disponible */}
        {annualAvailable && (
          <motion.div {...fade} className="flex flex-col items-center gap-3 mb-14">
            <div
              className="inline-flex items-center rounded-full border border-violet-light bg-[#F5F3FF] p-1"
              data-testid="pricing-toggle"
            >
              <button
                onClick={() => setAnnual(false)}
                data-testid="pricing-toggle-monthly"
                className={`rounded-full px-4 py-2.5 text-[15px] font-semibold transition-all duration-200 sm:px-6 ${
                  !annual
                    ? "bg-white text-ink shadow-sm"
                    : "text-ink-2 hover:text-ink"
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setAnnual(true)}
                data-testid="pricing-toggle-annual"
                className={`rounded-full px-4 py-2.5 text-[15px] font-semibold transition-all duration-200 sm:px-6 ${
                  annual
                    ? "bg-white text-ink shadow-sm"
                    : "text-ink-2 hover:text-ink"
                }`}
              >
                Anual
              </button>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EDF8F3] px-4 py-1.5 text-[13px] font-bold text-[#2D8A5E]">
              <Sparkles className="w-3.5 h-3.5" />
              Te ahorras 2 meses con el plan anual
            </span>
          </motion.div>
        )}

        {/* Demo banner */}
        <motion.div {...fade} className="mb-10 flex justify-center">
          <button
            type="button"
            onClick={openFreeModal}
            className="inline-flex items-center gap-3 rounded-2xl border border-violet-light bg-[#F5F3FF] px-6 py-3.5 text-[15px] font-semibold text-violet-fuerte transition-all duration-200 hover:border-violet-fuerte/40 hover:bg-[#EEEBFF]"
          >
            ¿Quieres explorar antes de comprar?
            <span className="font-bold underline underline-offset-2">Empezar gratis →</span>
          </button>
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 items-stretch gap-4 md:gap-5">
          {plans.map((plan, i) => {
            const planSlug = plan.name.toLowerCase();
            const isCenter = plan.highlight;
            const isGratis = plan.id === "gratis";
            const isInstitucional = planSlug === "institucional";
            // Gratis muestra su precio fijo (0); el resto, precio del backend.
            const price = isGratis ? plan.price : getPrice(planSlug);
            const regularPrice = getRegularPrice(planSlug);
            const annualTotal = getAnnualTotal(planSlug);

            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.75, delay: i * 0.08 }}
                className={`relative flex flex-col rounded-2xl transition-all duration-300 select-none ${
                  isCenter
                    ? "bg-ink text-white z-10 shadow-[0_24px_60px_-16px_rgba(26,17,53,0.35)]"
                    : "bg-white border border-violet-light"
                }`}
                data-testid={`pricing-card-${i}`}
              >
                {/* Recommended badge */}
                {isCenter && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gold px-5 py-1.5 text-sm font-bold text-ink whitespace-nowrap shadow-[0_4px_12px_-4px_rgba(201,162,39,0.4)]">
                      Recomendado
                    </span>
                  </div>
                )}

                <div className={`flex flex-col flex-1 p-6 lg:p-7 ${isCenter ? "pt-9 lg:pt-10" : ""}`}>
                  {/* Plan name */}
                  <p className={`text-base font-semibold tracking-wide ${
                    isCenter ? "text-violet-claro" : "text-ink-2"
                  }`}>
                    {plan.name}
                  </p>

                  {/* Price row */}
                  <div className="mt-5 flex items-baseline gap-1.5">
                    {loading && !isGratis ? (
                      <PriceSkeleton />
                    ) : (
                      <span
                        className={`font-display text-[clamp(2.25rem,8vw,3.8rem)] font-black leading-none tracking-tight lg:text-[3.8rem] ${
                          isCenter ? "text-white" : "text-ink"
                        }`}
                      >
                        {price != null ? `${plan.prefix}${price}` : "-"}
                      </span>
                    )}
                    <div className={`flex flex-col text-sm leading-tight ${
                      isCenter ? "text-ink-4" : "text-ink-3"
                    }`}>
                      <span className="font-semibold">EUR</span>
                      <span>/mes</span>
                      <span className="text-[11px] font-medium opacity-75">IVA incluido</span>
                    </div>
                  </div>
                  {/* Cargo real anual */}
                  {annual && annualAvailable && annualTotal && !isInstitucional && !isGratis && (
                    <p className={`mt-1 text-[12px] font-medium ${isCenter ? "text-white/40" : "text-ink-3"}`}>
                      {annualTotal}€ facturados al año
                    </p>
                  )}

                  {/* Promo or spacer */}
                  <div className="mt-3 min-h-[40px]">
                    {founderActive && !isInstitucional && !isGratis ? (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className={`select-none cursor-default text-[13px] font-bold ${isCenter ? "text-gold" : "text-gold-text"}`}>
                          Oferta Fundadores &middot; Precio bloqueado de por vida
                        </span>
                        {regularPrice && regularPrice !== price && (
                          <span className={`relative -top-0.5 text-[13px] line-through ${isCenter ? "text-white/30" : "text-ink-3"}`}>
                            {regularPrice}EUR/mes
                          </span>
                        )}
                      </div>
                    ) : isInstitucional ? (
                      <span className={`text-[13px] ${isCenter ? "text-ink-4" : "text-ink-3"}`}>
                        Contactar para presupuesto personalizado
                      </span>
                    ) : (
                      <div className="min-h-[20px]" />
                    )}
                  </div>

                  {/* Desc */}
                  <p className={`mt-2 text-[15px] leading-relaxed ${
                    isCenter ? "text-white/60" : "text-ink-2"
                  }`}>
                    {plan.desc}
                  </p>

                  {/* Beneficios fundador */}
                  {founderActive && !isInstitucional && !isGratis && (
                    <div className={`mt-5 space-y-3 rounded-xl px-4 py-3.5 ${
                      isCenter
                        ? "bg-white/[0.07] border border-white/10"
                        : "bg-[#FFFBEA] border border-gold/30"
                    }`}>
                      <p className={`text-[11px] font-bold uppercase tracking-[0.12em] ${isCenter ? "text-gold" : "text-gold-text"}`}>
                        Solo para las iglesias fundadoras
                      </p>
                      <div className="flex items-start gap-2.5">
                        <Clock className={`mt-0.5 h-4 w-4 shrink-0 ${isCenter ? "text-gold" : "text-gold-text"}`} strokeWidth={2.2} />
                        <p className={`text-[13px] leading-snug font-medium ${isCenter ? "text-white/70" : "text-ink-2"}`}>
                          <span className={isCenter ? "font-bold text-white" : "font-bold text-ink"}>Acceso anticipado de 30 días</span> a cada módulo nuevo, gratis y antes del lanzamiento oficial.
                        </p>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <BadgePercent className={`mt-0.5 h-4 w-4 shrink-0 ${isCenter ? "text-gold" : "text-gold-text"}`} strokeWidth={2.2} />
                        <p className={`text-[13px] leading-snug font-medium ${isCenter ? "text-white/70" : "text-ink-2"}`}>
                          <span className={isCenter ? "font-bold text-white" : "font-bold text-ink"}>50% de descuento permanente</span> en todos los módulos y mejoras que se lancen.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <button
                    type="button"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={loading && !isInstitucional && !isGratis}
                    data-testid={`pricing-cta-${i}`}
                    className={`mt-8 flex h-[52px] w-full items-center justify-center rounded-xl text-[17px] font-semibold transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 ${
                      isCenter
                        ? "bg-violet-fuerte text-white hover:bg-violet-medio shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)]"
                        : "bg-violet-fuerte text-white hover:bg-violet-oscuro shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)]"
                    }`}
                  >
                    {isGratis ? "Empezar gratis" : isInstitucional ? "Contactar" : "Elegir plan"}
                  </button>

                  {!isInstitucional && !isGratis && (
                    <button
                      type="button"
                      onClick={openFreeModal}
                      className={`mt-3 block w-full text-center text-[13px] font-medium transition-colors duration-200 ${
                        isCenter ? "text-white/40 hover:text-white/70" : "text-ink-3 hover:text-violet-fuerte"
                      }`}
                    >
                      o empezar gratis →
                    </button>
                  )}

                  {/* Divider */}
                  <div className={`mt-8 mb-6 h-px ${isCenter ? "bg-white/10" : "bg-violet-light"}`} />

                  {/* Features */}
                  <ul className="space-y-3 flex-1">
                    {plan.features.map((feat) => {
                      const featText = typeof feat === "string" ? feat : feat.text;
                      const featTooltip = typeof feat === "string" ? null : feat.tooltip;
                      return (
                        <li key={featText} className="flex items-start gap-3 text-[15px]">
                          <span className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${
                            isCenter ? "bg-white/10 text-violet-medio" : "bg-violet-light text-violet-fuerte"
                          }`}>
                            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                          </span>
                          <span className={`leading-snug ${isCenter ? "text-white/75" : "text-ink-2"}`}>
                            {featText}
                            {featTooltip && (
                              <span className="group/tip relative inline-flex items-center ml-1 cursor-default align-middle">
                                <Info className="w-3.5 h-3.5 opacity-50 shrink-0" />
                                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[200px] rounded-xl px-3 py-2 text-sm font-medium leading-snug bg-ink text-white opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-30 text-center shadow-xl">
                                  {featTooltip}
                                </span>
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Comparativa detallada de planes */}
        <motion.div {...fade} className="mt-14 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/comparativa-planes")}
            data-testid="pricing-compare-link"
            className="inline-flex items-center gap-2 rounded-xl border border-violet-light bg-white px-5 py-3 text-[15px] font-semibold text-violet-fuerte transition-all duration-200 hover:-translate-y-0.5 hover:border-violet-fuerte/40 hover:bg-[#F5F3FF]"
          >
            Compara planes en detalle
            <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </div>

      <FreePlanModal open={freeModalOpen} onClose={() => setFreeModalOpen(false)} />
      <ContactPlanModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        plan={contactPlan}
      />
    </section>
  );
}
